/**
 * guidedTour — a lightweight, dependency-free guided-tour engine (Vue 2 / vanilla).
 *
 * Highlights page elements one step at a time: a dimmed overlay with a
 * "spotlight" hole around the target + a small popover describing the step.
 * Ported from the Kairah Nuxt composable to plain JS so it works in this
 * Vue 2 / Vue CLI app with zero npm dependencies.
 *
 * Usage:
 *   import { startTour } from '@/utils/guidedTour'
 *   startTour([{ selector, title, body }, ...], { labels, onFinish })
 *
 * A step whose `selector` matches nothing is shown centered (no spotlight),
 * so textual-only steps are fine.
 */

const DEFAULT_LABELS = {
  next: 'Suivant', back: 'Précédent', done: 'Terminé', skip: 'Passer',
  step: 'Étape {n} / {total}',
}

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

let root = null
let backdrop = null
let popover = null
let steps = []
let index = 0
let labels = DEFAULT_LABELS
let onFinish
let keyHandler = null
let resizeHandler = null

function destroy() {
  if (keyHandler) window.removeEventListener('keydown', keyHandler)
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    window.removeEventListener('scroll', resizeHandler, true)
  }
  keyHandler = null
  resizeHandler = null
  if (root && root.remove) root.remove()
  root = backdrop = popover = null
  steps = []
  index = 0
}

function finish() {
  const cb = onFinish
  destroy()
  if (cb) cb()
}

function buildOverlay() {
  root = document.createElement('div')
  root.setAttribute('data-guided-tour', '')
  root.style.cssText = 'position:fixed;inset:0;z-index:9999;'

  backdrop = document.createElement('div')
  backdrop.style.cssText = [
    'position:absolute', 'border-radius:12px',
    'box-shadow:0 0 0 9999px rgba(15,18,40,0.55)',
    'pointer-events:none',
    reducedMotion() ? '' : 'transition:all .25s ease',
  ].filter(Boolean).join(';')

  popover = document.createElement('div')
  popover.tabIndex = -1
  popover.setAttribute('role', 'dialog')
  popover.setAttribute('aria-live', 'polite')
  popover.style.cssText = [
    'position:absolute', 'max-width:320px', 'width:calc(100vw - 32px)',
    'background:#ffffff', 'color:#1b1f3b', 'border:1px solid #e3e7f5',
    'border-radius:14px', 'box-shadow:0 24px 60px -20px rgba(49,46,129,0.45)',
    'padding:16px', 'pointer-events:auto', 'font-family:inherit',
    reducedMotion() ? '' : 'transition:all .25s ease',
  ].filter(Boolean).join(';')

  root.appendChild(backdrop)
  root.appendChild(popover)
  document.body.appendChild(root)
}

function place(el) {
  if (!backdrop || !popover) return
  const pad = 8
  const vw = window.innerWidth
  const vh = window.innerHeight

  if (!el) {
    backdrop.style.width = '0px'
    backdrop.style.height = '0px'
    backdrop.style.top = vh / 2 + 'px'
    backdrop.style.left = vw / 2 + 'px'
    popover.style.top = Math.max(16, vh / 2 - 120) + 'px'
    popover.style.left = Math.max(16, vw / 2 - 160) + 'px'
    return
  }

  const r = el.getBoundingClientRect()
  backdrop.style.top = r.top - pad + 'px'
  backdrop.style.left = r.left - pad + 'px'
  backdrop.style.width = r.width + pad * 2 + 'px'
  backdrop.style.height = r.height + pad * 2 + 'px'

  const pvH = popover.offsetHeight || 160
  const pvW = popover.offsetWidth || 320
  let top = r.bottom + pad + 8
  if (top + pvH > vh - 8) top = Math.max(8, r.top - pad - 8 - pvH)
  let left = r.left
  if (left + pvW > vw - 8) left = Math.max(8, vw - 8 - pvW)
  popover.style.top = top + 'px'
  popover.style.left = left + 'px'
}

function renderStep() {
  if (!popover) return
  const step = steps[index]
  const total = steps.length
  const isLast = index === total - 1
  const isFirst = index === 0
  const el = step && step.selector ? document.querySelector(step.selector) : null

  if (el) el.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' })
  place(el)

  const counter = labels.step.replace('{n}', String(index + 1)).replace('{total}', String(total))
  popover.innerHTML = ''

  const ctr = document.createElement('p')
  ctr.textContent = counter
  ctr.style.cssText = 'font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:#8c93b3;margin:0 0 6px'
  const h = document.createElement('h3')
  h.textContent = (step && step.title) || ''
  h.style.cssText = 'font-size:15px;font-weight:600;color:#312e81;margin:0 0 6px'
  const p = document.createElement('p')
  p.textContent = (step && step.body) || ''
  p.style.cssText = 'font-size:13.5px;line-height:1.5;color:#5b6386;margin:0 0 14px'

  const actions = document.createElement('div')
  actions.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px'

  const skip = document.createElement('button')
  skip.type = 'button'
  skip.textContent = labels.skip
  skip.style.cssText = 'font-size:12px;color:#8c93b3;background:none;border:none;cursor:pointer;padding:6px 4px'
  skip.onclick = finish

  const right = document.createElement('div')
  right.style.cssText = 'display:flex;align-items:center;gap:8px'

  if (!isFirst) {
    const back = document.createElement('button')
    back.type = 'button'
    back.textContent = labels.back
    back.style.cssText = 'font-size:13px;padding:7px 12px;border-radius:9px;border:1px solid #cfd6ef;background:#fff;color:#1b1f3b;cursor:pointer'
    back.onclick = () => go(index - 1)
    right.appendChild(back)
  }
  const next = document.createElement('button')
  next.type = 'button'
  next.textContent = isLast ? labels.done : labels.next
  next.style.cssText = 'font-size:13px;padding:7px 14px;border-radius:9px;border:none;background:linear-gradient(135deg,#6d5ef6,#2d8cff);color:#fff;cursor:pointer;font-weight:600'
  next.onclick = () => (isLast ? finish() : go(index + 1))
  right.appendChild(next)

  actions.appendChild(skip)
  actions.appendChild(right)
  popover.appendChild(ctr)
  popover.appendChild(h)
  popover.appendChild(p)
  popover.appendChild(actions)
  popover.focus()
}

function go(i) {
  index = Math.max(0, Math.min(i, steps.length - 1))
  renderStep()
}

/**
 * Start a guided tour.
 * @param {{selector?:string,title:string,body:string}[]} tourSteps
 * @param {{labels?:object,onFinish?:function}} [opts]
 */
export function startTour(tourSteps, opts = {}) {
  if (!tourSteps || !tourSteps.length) return
  destroy()
  steps = tourSteps
  labels = Object.assign({}, DEFAULT_LABELS, opts.labels || {})
  onFinish = opts.onFinish
  index = 0
  buildOverlay()

  keyHandler = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); finish() }
    else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); index === steps.length - 1 ? finish() : go(index + 1) }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1) }
  }
  resizeHandler = () => { const s = steps[index]; place(s && s.selector ? document.querySelector(s.selector) : null) }
  window.addEventListener('keydown', keyHandler)
  window.addEventListener('resize', resizeHandler)
  window.addEventListener('scroll', resizeHandler, true)

  go(0)
}

export function stopTour() { destroy() }
